// "App updates" — the only place a person can see whether OTA is working.
//
// WHY THIS EXISTS. `checkForUpdate` has six ways to return "nothing happened"
// (core/ota/otaClient.js) and, until now, every one of them looked identical
// from outside: no update appeared. That is how a broken OTA host went three
// weeks and eight bundles without anyone noticing — the failure mode of this
// subsystem is *looking exactly like success*, so it needs somewhere to say
// which of the six actually occurred.
//
// Three versions, because they answer three different questions and are
// routinely different numbers:
//
//   installed   the APK Play put on the phone     (getBuiltinVersion)
//   running     the bundle executing right now    (APP_VERSION, travels in the
//                                                  bundle, so it MOVES on OTA)
//   on server   what the manifest offers          (read live, here)
//
// running > installed means an OTA has been applied. running < on-server with a
// reason of "up to date" would be a contradiction worth reporting.

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, useTheme,
} from '@mui/material';
import { APP_VERSION, BUILD_DATE } from '../../../core/version';
import {
  checkForUpdate, loadUpdater, lastOtaResult, otaSupported, OTA_BASE, OTA_REASON,
} from '../../../core/ota/otaClient';

// Plain English for each outcome, and — where it is a fault — WHOSE fault and
// what fixes it. A user reading "the update server is not serving the update
// file" can report something actionable; "no updates found" produces nothing.
const EXPLAIN = {
  [OTA_REASON.UP_TO_DATE]: 'You are on the latest version.',
  [OTA_REASON.STAGED]: 'Update downloaded — close the app completely and reopen it to finish.',
  [OTA_REASON.NEEDS_NEWER_APP]: 'An update exists but needs a newer app from the Play Store.',
  [OTA_REASON.UNSUPPORTED]: 'Live updates only run in the installed Android app.',
  [OTA_REASON.UNREACHABLE]: 'Could not reach the update server. Check the connection.',
  [OTA_REASON.NOT_JSON]: 'The update server is serving a web page instead of the update file — '
    + 'the update has not been published correctly. Report this.',
  [OTA_REASON.MALFORMED]: 'The update server sent a file this app cannot read. Report this.',
  [OTA_REASON.ERROR]: 'The update check failed.',
};

function Row({ label, value, dim }) {
  const c = useTheme().sli;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
      <Typography sx={{ color: c.text, opacity: 0.7, fontSize: 14 }}>{label}</Typography>
      <Typography sx={{
        color: c.text, opacity: dim ? 0.7 : 1, fontSize: 14, fontWeight: dim ? 400 : 700, textAlign: 'right',
      }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function AppUpdateDialog({ open, onClose }) {
  const theme = useTheme();
  const c = theme.sli;
  const [installed, setInstalled] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(lastOtaResult());

  // The APK's own version — only knowable through the plugin, and only on a
  // native build. On web it stays blank rather than claiming a number.
  useEffect(() => {
    if (!open || !otaSupported()) return;
    loadUpdater()
      .then(({ updater }) => updater.getBuiltinVersion())
      .then((v) => setInstalled(v && v.version ? v.version : ''))
      .catch(() => setInstalled(''));
  }, [open]);

  const check = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { updater: CapacitorUpdater } = await loadUpdater();
      await checkForUpdate(CapacitorUpdater);
    } catch (e) { /* the reason is recorded either way */ } finally {
      setResult({ ...lastOtaResult() });
      setBusy(false);
    }
  };

  const reason = result && result.reason;
  const staged = reason === OTA_REASON.STAGED;
  // Every reason except these two is a fault worth showing in red — including
  // "not JSON", which is the one that hid the last outage.
  const bad = reason && reason !== OTA_REASON.UP_TO_DATE && !staged;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{ sx: { bgcolor: c.back, borderRadius: '20px' } }}
    >
      <DialogTitle sx={{ color: c.text, fontWeight: 700 }}>App updates</DialogTitle>
      <DialogContent>
        <Row label="Running now" value={APP_VERSION || '—'} />
        {installed && installed !== APP_VERSION && (
          <Row label="Installed from Play" value={installed} dim />
        )}
        <Row label="Built" value={BUILD_DATE || '—'} dim />
        {result && result.serverVersion && (
          <Row label="On the server" value={result.serverVersion} dim />
        )}

        {reason && (
          <Box sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: '12px',
            // eslint-disable-next-line no-nested-ternary
            bgcolor: bad ? '#C6282822' : (staged ? '#2E7D3222' : 'transparent'),
            border: `1px solid ${bad ? '#C62828' : (staged ? '#2E7D32' : `${c.text}33`)}`,
          }}
          >
            <Typography sx={{ color: c.text, fontSize: 14 }}>
              {EXPLAIN[reason] || reason}
            </Typography>
            {/* The raw detail, because the useful half of a report is the part
                the reporter cannot paraphrase. */}
            {bad && result.detail && (
              <Typography sx={{ color: c.text, opacity: 0.6, fontSize: 11, mt: 0.75, wordBreak: 'break-all' }}>
                {result.detail}
              </Typography>
            )}
          </Box>
        )}

        {!otaSupported() && (
          <Typography sx={{ color: c.text, opacity: 0.6, fontSize: 12, mt: 1.5 }}>
            {OTA_BASE
              ? 'Live updates run only in the installed Android app.'
              : 'No update server is configured in this build.'}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: c.text }}>Close</Button>
        <Button
          onClick={check}
          disabled={busy || !otaSupported()}
          startIcon={busy ? <CircularProgress size={16} /> : null}
          sx={{ color: c.text, fontWeight: 700 }}
        >
          {busy ? 'Checking…' : 'Check now'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
